// NoteDetailCard — right-side card for the Notes scene. Shows a list
// of recent notes (and an excerpt card when one is opened).

import { useEffect, useState } from 'react';
import {
  NOTES,
  type NoteConfig,
  type SceneConfig,
} from './planetSections';
import { Magnetic } from './Magnetic';
import { openDetail } from './SatelliteInteraction';

// ---------------------------------------------------------------------------
// Subscribers
// ---------------------------------------------------------------------------

interface NoteOpenDetail {
  id: number;
}

declare global {
  interface WindowEventMap {
    'note:open': CustomEvent<NoteOpenDetail>;
    'note:close': CustomEvent<null>;
  }
}

export interface NoteDetailCardProps {
  activeScene: SceneConfig;
  openByDefault?: boolean;
}

export function NoteDetailCard({
  activeScene,
  openByDefault,
}: NoteDetailCardProps) {
  const [openId, setOpenId] = useState<number | null>(null);

  useEffect(() => {
    const onOpen = (e: Event) => {
      const detail = (e as CustomEvent<NoteOpenDetail>).detail;
      setOpenId(detail?.id ?? null);
    };
    const onClose = () => setOpenId(null);
    if (typeof window !== 'undefined') {
      window.addEventListener('note:open', onOpen as EventListener);
      window.addEventListener('note:close', onClose as EventListener);
    }
    return () => {
      if (typeof window !== 'undefined') {
        window.removeEventListener('note:open', onOpen as EventListener);
        window.removeEventListener('note:close', onClose as EventListener);
      }
    };
  }, []);

  const note = openId !== null
    ? NOTES.find((n) => n.id === openId) ?? null
    : null;
  const isOpen = !!note || (openByDefault && NOTES.length > 0);

  return (
    <aside
      className={['note-card', isOpen ? 'is-open' : ''].join(' ')}
      aria-label="Note"
      aria-hidden={!isOpen}
    >
      {note ? (
        <NoteDetail note={note} onClose={() => setOpenId(null)} />
      ) : (
        <NoteList onPick={(id) => setOpenId(id)} activeScene={activeScene} />
      )}
    </aside>
  );
}

// ---------------------------------------------------------------------------
// NoteDetail — the open card.
// ---------------------------------------------------------------------------

function NoteDetail({
  note,
  onClose,
}: {
  note: NoteConfig;
  onClose: () => void;
}) {
  // Excerpt — from the config, with a generic fallback.
  const excerpt =
    note.excerpt ??
    `${note.title} — a short piece, part of the ongoing collection of notes on tools, interfaces, and the slower side of building software.`;
  return (
    <div
      style={{
        background: 'rgba(255,255,255,0.04)',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        border: '1px solid rgba(255,255,255,0.08)',
        borderRadius: 16,
        padding: 24,
        boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
        position: 'relative',
      }}
    >
      <Magnetic
        as="button"
        className="hud-panel__close"
        ariaLabel="Close note detail"
        onClick={onClose}
        type="button"
        innerClassName="hud-panel__close-inner"
      >
        <span aria-hidden="true">×</span>
      </Magnetic>
      <div className="hud-panel__kicker">Note · {note.read}</div>
      <h3 className="hud-panel__title">{note.title}</h3>
      <p
        className="hud-panel__year"
        style={{ marginBottom: 12, display: 'inline-block' }}
      >
        {note.date}
      </p>
      <p className="hud-panel__body">{excerpt}</p>
      <div className="hud-panel__divider" />
      <div className="hud-panel__tags">
        <Magnetic
          as="span"
          className="hud-panel__tag"
          innerClassName="hud-panel__tag-inner"
          ariaLabel={`Tag: ${note.tag}`}
        >
          {note.tag}
        </Magnetic>
        {note.tags?.map((t) => (
          <Magnetic
            key={t}
            as="span"
            className="hud-panel__tag"
            innerClassName="hud-panel__tag-inner"
            ariaLabel={`Tag: ${t}`}
          >
            {t}
          </Magnetic>
        ))}
      </div>
      <div style={{ marginTop: 14, display: 'flex', justifyContent: 'flex-end' }}>
        <Magnetic
          as="button"
          className="hud-panel__readmore"
          ariaLabel="Read full note"
          type="button"
          onClick={() => openDetail({ type: 'note', id: String(note.id) })}
          innerClassName="hud-panel__readmore-inner"
        >
          <span>Read full</span>
          <span aria-hidden="true" className="hud-panel__readmore-arrow">→</span>
        </Magnetic>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// NoteList — list of notes shown when no individual note is picked.
// ---------------------------------------------------------------------------

function NoteList({
  onPick,
  activeScene,
}: {
  onPick: (id: number) => void;
  activeScene: SceneConfig;
}) {
  return (
    <div
      style={{
        background: 'rgba(255,255,255,0.04)',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        border: '1px solid rgba(255,255,255,0.08)',
        borderRadius: 16,
        padding: 24,
        boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
      }}
    >
      <div className="hud-panel__kicker">Notes</div>
      <h3 className="hud-panel__title">{activeScene.title}</h3>
      <p className="hud-panel__subtitle">{activeScene.subtitle}</p>
      <div className="hud-panel__divider" />
      <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'grid', gap: 12 }}>
        {NOTES.map((n) => (
          <li key={n.id}>
            <Magnetic
              as="button"
              className="note-row"
              ariaLabel={`Open note: ${n.title}`}
              onClick={() => onPick(n.id)}
              type="button"
              innerClassName="note-row-inner"
            >
              <span style={{ display: 'flex', justifyContent: 'space-between', gap: 12, width: '100%' }}>
                <span
                  className="t-mono"
                  style={{
                    color: 'var(--accent-cool)',
                    fontSize: 11,
                    letterSpacing: '0.18em',
                    textTransform: 'uppercase',
                  }}
                >
                  {n.date}
                </span>
                <span
                  className="t-mono"
                  style={{
                    color: 'var(--text-muted)',
                    fontSize: 10,
                    letterSpacing: '0.18em',
                    textTransform: 'uppercase',
                  }}
                >
                  {n.read}
                </span>
              </span>
              <span
                style={{
                  fontFamily: 'var(--font-display)',
                  fontSize: 17,
                  color: 'var(--text-primary)',
                  letterSpacing: '-0.01em',
                  lineHeight: 1.25,
                  textAlign: 'left',
                }}
              >
                {n.title}
              </span>
              <span style={{ marginTop: 4, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                <span className="hud-panel__tag" style={{ cursor: 'default' }}>{n.tag}</span>
              </span>
            </Magnetic>
          </li>
        ))}
      </ul>
    </div>
  );
}
